import { useCallback, useEffect, useMemo } from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';
import { useLayoutComponentEditorStore } from './useLayoutComponentEditorStore';
import { ILayoutComponentEditorProps } from './LayoutComponentEditor.types';
import { ConfigureDrawerContent } from '../../ConfigureDrawer.styles';
import { ConfigureDrawerContentLoader } from '../../ConfigureDrawerContentLoader';
import { useFlowBuilder2PageStore } from '../../../../Store/useFlowBuilder2PageStore';
import {
  useLayoutComponentSettings,
  useEditorStore,
  useFlowBuilder2Store,
  ILayoutComponentConfiguratorEditor,
  useAssistantLoadingStore,
  useLayoutComponentPropertyEditorMap,
  ILayoutComponentPropertyEditorProps,
} from '@diana/core';
import { LAYOUT_COMPONENTS_NS } from '../../../../../../i18n/layoutComponents';

export const LayoutComponentEditorContent = (props: ILayoutComponentEditorProps) => {
  const { value } = props;

  const { t } = useTranslation(LAYOUT_COMPONENTS_NS);

  const { getSettings } = useLayoutComponentSettings();

  const isTemporary = useEditorStore((s) => !!s.temporaryLayoutComponentIdsMap[value.id]);

  const settings = useMemo(() => getSettings(value), [value, getSettings]);
  const displayName = settings?.displayName ?? value.component;
  const editors = settings?.editors ?? [];
  const hasEditors = editors.length > 0;

  return (
    <ConfigureDrawerContent>
      {isTemporary ? (
        <ConfigureDrawerContentLoader />
      ) : hasEditors ? (
        <Stack gap={1.5}>
          {editors.map((editor, index) => {
            return <PropertyEditor key={`${value.id}-${index}`} {...props} editor={editor} />;
          })}
        </Stack>
      ) : (
        <Typography>{t('descriptions.missingConfigurators', { name: displayName })}</Typography>
      )}
    </ConfigureDrawerContent>
  );
};

const PropertyEditor = (
  props: ILayoutComponentEditorProps & { editor: ILayoutComponentConfiguratorEditor },
) => {
  const {
    flowId,
    layoutId,
    componentIndex,
    value,
    onChange: _onChange,
    data,
    onDataChange,
    editor,
  } = props;

  const setFieldState = useFlowBuilder2Store((s) => s.setFieldState);
  const fieldState = useFlowBuilder2Store((s) => {
    return s.getFieldState(value.id, 'layoutComponent', editor.name);
  }, isEqual);

  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);

  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const openSubEditor = useLayoutComponentEditorStore((s) => s.openSubEditor);
  const onCloseSubEditor = useLayoutComponentEditorStore((s) => s.closeSubEditor);

  const propertyEditorMap = useLayoutComponentPropertyEditorMap();

  const module = propertyEditorMap[editor.name];
  const disabled = hasAnyPendingStatus || isChatLoading;

  const options = useMemo(() => {
    return editor.options ?? {};
  }, [editor.options]);

  const onChange = useCallback<ILayoutComponentPropertyEditorProps['onChange']>(
    (component) => {
      if ([!fieldState.dirty, !fieldState.touched].some(Boolean)) {
        setFieldState(component.id, 'layoutComponent', editor.name, (draft) => {
          draft.dirty = true;
          draft.touched = true;
          return draft;
        });
      }
      _onChange(component);
    },
    [fieldState.dirty, fieldState.touched, _onChange, setFieldState, editor.name],
  );

  const onTouched = useCallback<ILayoutComponentPropertyEditorProps['onTouched']>(() => {
    if (!fieldState.touched) {
      setFieldState(value.id, 'layoutComponent', editor.name, (draft) => {
        draft.touched = true;
        return draft;
      });
    }
  }, [fieldState.touched, setFieldState, value.id, editor.name]);

  const onOpenSubEditor = useCallback<ILayoutComponentPropertyEditorProps['onOpenSubEditor']>(
    (useSubEditor) => {
      openSubEditor({ useSubEditor, editor });
    },
    [openSubEditor, editor],
  );

  useEffect(() => {
    return () => {
      setFieldState(value.id, 'layoutComponent', editor.name, (draft) => {
        draft.dirty = true;
        draft.touched = true;
        return draft;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <module.component
      key={`${value.id}-${componentIndex}`}
      flowId={flowId}
      layoutId={layoutId}
      disabled={disabled}
      componentIndex={componentIndex}
      options={options}
      dirty={fieldState.dirty}
      touched={fieldState.touched}
      value={value}
      onChange={onChange}
      data={data}
      onDataChange={onDataChange}
      onTouched={onTouched}
      onOpenSubEditor={onOpenSubEditor}
      onCloseSubEditor={onCloseSubEditor}
    />
  );
};
